import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type CandidateHit, type Job, type Pool, type PoolMember } from "../lib/api";
import { whenLast } from "../lib/dates";
import { useTitle } from "../lib/title";
import { people } from "./AddToPool";
import { candidateLine } from "./Candidates";

const pad = (n: number) => String(n).padStart(2, "0");

export function PoolPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [pool, setPool] = useState<Pool | null>(null);
  const [members, setMembers] = useState<PoolMember[]>([]);
  useTitle(pool?.name ?? "Pool");
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [message, setMessage] = useState<{ text: string; jobId?: string } | null>(null);
  const load = useAction();
  const act = useAction();

  const refresh = async () => {
    const r = await api.pool(id);
    setPool(r.pool);
    setMembers(r.members);
  };
  useEffect(() => {
    setPool(null);
    void load.run(refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!pool) {
    return (
      <>
        <p className="label">
          <Link to="/pools">← Talent pools</Link>
        </p>
        <ErrorNotice error={load.error} />
        {!load.error && <p className="label">Loading…</p>}
      </>
    );
  }

  const remove = (m: PoolMember) =>
    void act.run(async () => {
      await api.removeFromPool(pool.id, m.id);
      setMessage({ text: `Took ${m.fullName} out of this pool.` });
      await refresh();
    });

  return (
    <>
      <div className="page-head">
        <p className="label">
          <Link to="/pools">← Talent pools</Link>
        </p>
        <h1 className="display">{pool.name}</h1>
        {pool.description && <p className="lede">{pool.description}</p>}
      </div>

      {editing && (
        <PoolForm
          pool={pool}
          onCancel={() => setEditing(false)}
          onSaved={(p) => {
            setPool(p);
            setEditing(false);
          }}
        />
      )}

      {message && (
        <p className="notice ok" role="status">
          {message.text} {message.jobId && <Link to={`/jobs/${message.jobId}`}>Open the job</Link>}
        </p>
      )}
      <ErrorNotice error={act.error} />

      <section className="section" aria-labelledby="members-h">
        <div className="section-head">
          <h2 id="members-h" className="label">
            In this pool <span className="figures">· {pad(members.length)}</span>
          </h2>
          <span className="button-row">
            {!editing && (
              <button type="button" className="link-btn" onClick={() => setEditing(true)}>
                Rename or describe
              </button>
            )}
            {!confirmDelete ? (
              <button type="button" className="link-btn danger" onClick={() => setConfirmDelete(true)}>
                Delete pool
              </button>
            ) : (
              <>
                <span className="label alert">Delete {pool.name}? The candidates stay; only the list goes.</span>
                <button
                  type="button"
                  className="link-btn danger"
                  disabled={act.pending}
                  onClick={() =>
                    void act.run(async () => {
                      await api.deletePool(pool.id);
                      navigate("/pools");
                    })
                  }
                >
                  Delete
                </button>
                <button type="button" className="link-btn" onClick={() => setConfirmDelete(false)}>
                  Keep
                </button>
              </>
            )}
          </span>
        </div>

        <ol className="index">
          {members.map((m, i) => (
            <MemberRow key={m.id} index={i} member={m} poolId={pool.id} busy={act.pending} onRemove={() => remove(m)} />
          ))}
          {members.length === 0 && (
            <li>
              <span className="empty">No one in this pool yet. Find people below, or search Candidates and add everyone listed.</span>
            </li>
          )}
        </ol>
      </section>

      <FindPeople
        poolId={pool.id}
        memberIds={members.map((m) => m.id)}
        onAdded={async (name) => {
          setMessage({ text: `Added ${name}.` });
          await refresh();
        }}
      />

      {members.length > 0 && <PutOnJob pool={pool} onDone={(text, jobId) => setMessage({ text, jobId })} />}
    </>
  );
}

function MemberRow({ member: m, index, poolId, busy, onRemove }: { member: PoolMember; index: number; poolId: string; busy: boolean; onRemove: () => void }) {
  const [note, setNote] = useState(m.pool.note);
  const [editing, setEditing] = useState(false);
  const save = useAction();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save.run(async () => {
      await api.setPoolNote(poolId, m.id, note.trim());
      setNote(note.trim());
      setEditing(false);
    });
  };
  return (
    <li className={m.status === "inactive" ? "inactive" : undefined}>
      <span className="num" aria-hidden>
        {pad(index + 1)}
      </span>
      <span className="main">
        <Link className="primary" to={`/candidates/${m.id}`}>
          {m.fullName}
        </Link>
        <span className="secondary">{candidateLine(m)}</span>
        {editing ? (
          <form className="pool-note" onSubmit={submit}>
            <label className="field compact">
              <span className="sr-only">Why {m.fullName} is in this pool</span>
              <input value={note} maxLength={500} autoFocus onChange={(e) => setNote(e.target.value)} />
            </label>
            <button className="link-btn" disabled={save.pending}>
              Save
            </button>
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setNote(m.pool.note);
                setEditing(false);
              }}
            >
              Cancel
            </button>
            <ErrorNotice error={save.error} />
          </form>
        ) : (
          note && <span className="prewrap">{note}</span>
        )}
        <span className="secondary">
          {m.status === "inactive" && "Inactive · "}
          Added {whenLast(m.pool.addedAt)}
          {m.pool.addedBy && ` by ${m.pool.addedBy}`}
        </span>
      </span>
      <span className="actions button-row">
        {!editing && (
          <button type="button" className="link-btn" onClick={() => setEditing(true)} aria-label={`${note ? "Edit note" : "Add a note"} for ${m.fullName}`}>
            {note ? "Edit note" : "Add note"}
          </button>
        )}
        <button type="button" className="link-btn" disabled={busy} onClick={onRemove} aria-label={`Take ${m.fullName} out of this pool`}>
          Remove
        </button>
      </span>
    </li>
  );
}

function PoolForm({ pool, onCancel, onSaved }: { pool: Pool; onCancel: () => void; onSaved: (p: Pool) => void }) {
  const [name, setName] = useState(pool.name);
  const [description, setDescription] = useState(pool.description);
  const save = useAction();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save.run(async () => onSaved((await api.updatePool(pool.id, { name: name.trim(), description: description.trim() })).pool));
  };
  return (
    <form className="form panel" onSubmit={submit} aria-label="Rename pool">
      <label className="field">
        <span className="label">Name</span>
        <input value={name} maxLength={100} required onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span className="label">What it's for</span>
        <input value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <ErrorNotice error={save.error} />
      <div className="button-row">
        <button className="btn" disabled={save.pending || !name.trim()}>
          {save.pending ? "Saving…" : "Save"}
        </button>
        <button type="button" className="link-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** A quick name or skill search to add one person at a time. */
function FindPeople({ poolId, memberIds, onAdded }: { poolId: string; memberIds: string[]; onAdded: (name: string) => Promise<void> }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<CandidateHit[] | null>(null);
  const search = useAction();
  const add = useAction();

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) return setHits(null);
    let current = true;
    const timer = setTimeout(() => {
      void search.run(async () => {
        const r = await api.candidates({ q: query, status: "all" });
        if (current) setHits(r.candidates.slice(0, 8));
      });
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <section className="section" aria-labelledby="find-h">
      <div className="section-head">
        <h2 id="find-h" className="label">
          Add people
        </h2>
      </div>
      <label className="field compact narrow-field">
        <span className="label">Find a candidate</span>
        <input type="search" autoComplete="off" value={q} placeholder="Name, skill or title" onChange={(e) => setQ(e.target.value)} />
      </label>
      <ErrorNotice error={search.error ?? add.error} />
      {hits && (
        <ul className="index compact-list">
          {hits.map((c) => {
            const inPool = memberIds.includes(c.id);
            return (
              <li key={c.id}>
                <span className="main">
                  <span className="primary">{c.fullName}</span>
                  <span className="secondary">{candidateLine(c)}</span>
                </span>
                <span className="actions">
                  {inPool ? (
                    <span className="label">In this pool</span>
                  ) : (
                    <button
                      type="button"
                      className="link-btn"
                      disabled={add.pending}
                      aria-label={`Add ${c.fullName} to this pool`}
                      onClick={() =>
                        void add.run(async () => {
                          await api.addToPool(poolId, [c.id]);
                          await onAdded(c.fullName);
                        })
                      }
                    >
                      Add
                    </button>
                  )}
                </span>
              </li>
            );
          })}
          {hits.length === 0 && (
            <li>
              <span className="empty">No one matches.</span>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

/** Puts everyone in the pool on a job's pipeline at once. */
function PutOnJob({ pool, onDone }: { pool: Pool; onDone: (message: string, jobId: string) => void }) {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [jobId, setJobId] = useState("");
  const load = useAction();
  const save = useAction();

  useEffect(() => {
    void load.run(async () => {
      const [open, onHold] = await Promise.all([api.jobs({ status: "open" }), api.jobs({ status: "on_hold" })]);
      const list = [...open.jobs, ...onHold.jobs];
      setJobs(list);
      setJobId(list[0]?.id ?? "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save.run(async () => {
      const r = await api.poolToJob(pool.id, jobId);
      const job = jobs!.find((j) => j.id === jobId)!;
      const extra = [r.alreadyOnJob > 0 && `${people(r.alreadyOnJob)} already on it`, r.inactive > 0 && `${people(r.inactive)} inactive, left out`].filter(Boolean);
      onDone(`Added ${people(r.added)} to ${job.title} at Shortlisted${extra.length ? ` (${extra.join(", ")})` : ""}.`, jobId);
    });
  };

  return (
    <section className="section" aria-labelledby="to-job-h">
      <div className="section-head">
        <h2 id="to-job-h" className="label">
          Put this pool on a job
        </h2>
      </div>
      <ErrorNotice error={load.error} />
      {jobs && jobs.length === 0 && <p className="empty-line">No open jobs. Open one from Jobs first.</p>}
      {jobs && jobs.length > 0 && (
        <form className="form narrow" onSubmit={submit}>
          <label className="field">
            <span className="label">Job</span>
            <select value={jobId} onChange={(e) => setJobId(e.target.value)}>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title} · {j.company.name}
                </option>
              ))}
            </select>
          </label>
          <ErrorNotice error={save.error} />
          <div className="button-row">
            <button className="btn secondary" disabled={save.pending || !jobId}>
              {save.pending ? "Adding…" : `Add ${people(pool.size)} to this job`}
            </button>
            <span className="hint">They start at Shortlisted. Anyone already on the job stays where they are.</span>
          </div>
        </form>
      )}
    </section>
  );
}
